// ---------------------------------------------------------------------------
// HttpClientContext — Provides a pre-configured IHttpClient via React context.
//
// The provider creates the client once using useAuth().getToken, so consumers
// never need to call useAuth() or createApiClient() directly.
//
// Usage:
//   // In a hook or component:
//   const client = useHttpClient();
//   const service = new JudgeService(client);
// ---------------------------------------------------------------------------

import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
} from 'react';

import { config } from '../config';
import { useAuth } from '../hooks/useAuth/useAuth';

import { createApiClient, type IHttpClient } from './client';

export const HttpClientContext = createContext<IHttpClient | null>(null);

export function HttpClientProvider({ children }: PropsWithChildren) {
  const { getToken } = useAuth();

  const client = useMemo(() => {
    // In local dev with the mock Workflow API the token request would fail
    // (the API scope is not consented for the test account) and trigger an
    // unwanted MSAL redirect. Skip the token entirely — the API's
    // AnonymousLocalAuthHandler accepts unauthenticated requests.
    const tokenFn = config.apiAuthEnabled
      ? getToken
      : () => Promise.resolve('');
    return createApiClient(tokenFn);
  }, [getToken]);

  return (
    <HttpClientContext.Provider value={client}>
      {children}
    </HttpClientContext.Provider>
  );
}

export function useHttpClient(): IHttpClient {
  const client = useContext(HttpClientContext);
  if (!client) {
    throw new Error('useHttpClient must be used within <HttpClientProvider>');
  }
  return client;
}
